import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { formatINR } from "@/lib/roles";
import { Loader2, Calculator, Building } from "lucide-react";

export default function BuildingPlanNew() {
  const { user } = useAuth();
  const nav = useNavigate();
  const isCitizen = user?.role === "citizen";
  const [wards, setWards] = useState([]);
  const [bodies, setBodies] = useState([]);
  const [form, setForm] = useState({
    applicant_name: user?.name || "",
    applicant_email: user?.email || "",
    applicant_phone: user?.phone || "",
    architect_name: "",
    architect_license_no: "",
    property_type: "residential",
    purpose: "new_construction",
    plot_area: 2000,
    built_up_area: 1500,
    floors: 2,
    estimated_cost: 3000000,
    address: "",
    ward: "",
    zone: "North",
    municipal_body: "",
    latitude: 30.9010,
    longitude: 75.8573,
  });
  const [loading, setLoading] = useState(false);
  const [fee, setFee] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/master/wards"), api.get("/master/municipal-bodies")]).then(([a, b]) => {
      setWards(a.data.items || []);
      setBodies(b.data.items || []);
      setForm((f) => ({ ...f, ward: a.data.items?.[0] || "", municipal_body: b.data.items?.[0] || "" }));
    });
  }, []);

  // Live fee calculation client-side (mirrors backend logic)
  useEffect(() => {
    const base = Number(form.built_up_area) * 5;
    const mult = { new_construction: 1.0, renovation: 0.6, addition: 0.8 }[form.purpose] || 1.0;
    setFee(Math.max(1000, base * mult));
  }, [form.built_up_area, form.purpose]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form,
        plot_area: Number(form.plot_area),
        built_up_area: Number(form.built_up_area),
        floors: Number(form.floors),
        estimated_cost: Number(form.estimated_cost),
        latitude: Number(form.latitude),
        longitude: Number(form.longitude),
      };
      const { data } = await api.post("/building-plans", payload);
      toast.success(`Application ${data.application_id} submitted`);
      nav(isCitizen ? `/portal/building-plans/${data.id}` : `/building-plans/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
    setLoading(false);
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="bp-new-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Building Plan Approval</div>
        <h1 className="text-2xl sm:text-3xl font-heading font-semibold flex items-center gap-2">
          <Building className="w-6 h-6 text-[#0F2942] dark:text-white" /> New application
        </h1>
        <p className="text-sm text-slate-500 mt-1">Submit the application first. You can upload drawings and pay the scrutiny fee from the detail page.</p>
      </div>
      <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-base font-heading">Applicant & Architect</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><Label>Applicant name*</Label><Input required value={form.applicant_name} onChange={(e) => setForm({ ...form, applicant_name: e.target.value })} data-testid="bp-applicant-name" /></div>
              <div><Label>Email</Label><Input type="email" value={form.applicant_email} onChange={(e) => setForm({ ...form, applicant_email: e.target.value })} /></div>
              <div><Label>Phone</Label><Input value={form.applicant_phone} onChange={(e) => setForm({ ...form, applicant_phone: e.target.value })} /></div>
              <div />
              <div><Label>Architect name</Label><Input value={form.architect_name} onChange={(e) => setForm({ ...form, architect_name: e.target.value })} data-testid="bp-architect-name" /></div>
              <div><Label>Architect license #</Label><Input value={form.architect_license_no} onChange={(e) => setForm({ ...form, architect_license_no: e.target.value })} /></div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base font-heading">Property & construction</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><Label>Property type</Label>
                <Select value={form.property_type} onValueChange={(v) => setForm({ ...form, property_type: v })}>
                  <SelectTrigger data-testid="bp-property-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="residential">Residential</SelectItem>
                    <SelectItem value="commercial">Commercial</SelectItem>
                    <SelectItem value="industrial">Industrial</SelectItem>
                    <SelectItem value="mixed">Mixed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Purpose</Label>
                <Select value={form.purpose} onValueChange={(v) => setForm({ ...form, purpose: v })}>
                  <SelectTrigger data-testid="bp-purpose"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new_construction">New Construction</SelectItem>
                    <SelectItem value="renovation">Renovation</SelectItem>
                    <SelectItem value="addition">Addition / Alteration</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Plot area (sq.ft)*</Label><Input required type="number" value={form.plot_area} onChange={(e) => setForm({ ...form, plot_area: e.target.value })} /></div>
              <div><Label>Built-up area (sq.ft)*</Label><Input required type="number" value={form.built_up_area} onChange={(e) => setForm({ ...form, built_up_area: e.target.value })} data-testid="bp-built-up" /></div>
              <div><Label>Floors</Label><Input type="number" value={form.floors} onChange={(e) => setForm({ ...form, floors: e.target.value })} /></div>
              <div><Label>Estimated cost (₹)</Label><Input type="number" value={form.estimated_cost} onChange={(e) => setForm({ ...form, estimated_cost: e.target.value })} /></div>
              <div className="md:col-span-2"><Label>Address*</Label><Input required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="bp-address" /></div>
              <div><Label>Municipal body</Label>
                <Select value={form.municipal_body} onValueChange={(v) => setForm({ ...form, municipal_body: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{bodies.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Ward</Label>
                <Select value={form.ward} onValueChange={(v) => setForm({ ...form, ward: v })}>
                  <SelectTrigger data-testid="bp-ward"><SelectValue /></SelectTrigger>
                  <SelectContent>{wards.map((w) => <SelectItem key={w} value={w}>{w}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Zone</Label>
                <Select value={form.zone} onValueChange={(v) => setForm({ ...form, zone: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{["North", "South", "East", "West", "Central"].map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Latitude</Label><Input type="number" step="0.00001" value={form.latitude} onChange={(e) => setForm({ ...form, latitude: e.target.value })} /></div>
              <div><Label>Longitude</Label><Input type="number" step="0.00001" value={form.longitude} onChange={(e) => setForm({ ...form, longitude: e.target.value })} /></div>
            </CardContent>
          </Card>
        </div>

        <div>
          <Card className="sticky top-24" data-testid="bp-fee-card">
            <CardHeader><CardTitle className="text-base font-heading flex items-center gap-2"><Calculator className="w-4 h-4" /> Scrutiny fee</CardTitle></CardHeader>
            <CardContent>
              <div className="text-xs uppercase tracking-wider text-slate-500">Estimated fee</div>
              <div className="text-3xl font-heading font-bold tabular-nums text-[#0F2942] dark:text-white mt-1" data-testid="bp-fee-amount">
                {fee != null ? formatINR(fee) : "—"}
              </div>
              <ul className="mt-4 space-y-1 text-xs text-slate-500">
                <li>Base: ₹5 / sq.ft built-up area</li>
                <li>Multiplier by purpose: new 1.0×, addition 0.8×, renovation 0.6×</li>
                <li>Minimum fee: ₹1,000</li>
              </ul>
              <Button type="submit" disabled={loading} className="w-full mt-6 bg-[#0F2942] hover:bg-[#1E3A5F]" data-testid="btn-submit-bp">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit application"}
              </Button>
              <p className="mt-3 text-xs text-slate-500 text-center">You can upload drawings after submission.</p>
            </CardContent>
          </Card>
        </div>
      </form>
    </div>
  );
}
